import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../../app/i18n/LanguageContext';
import EmptyState from './EmptyState';
import LoadingState from './LoadingState';

const baseEnvironment = process.env.REACT_APP_API_BASE_URL?.trim() || 'http://localhost:8010';
const API_BASE_URL = baseEnvironment.replace(/\/$/, '');

const buildHeaders = () => {
  const token = sessionStorage.getItem('authToken');
  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

const getErrorMessage = (payload, fallback) => {
  if (payload instanceof Error) {
    return payload.message;
  }
  return fallback;
};

const UI_TEXT = {
  'tr-TR': {
    period: 'Donem',
    company: 'Sirket',
    loading: 'Yukleniyor...',
    companyNotFound: 'Sirket bulunamadi',
    periodNotFound: 'Donem bulunamadi',
    companiesLoadError: 'Sirketler yuklenemedi.',
    companiesLoadFailed: 'Sirketler alinirken hata olustu.',
    periodsLoadError: 'Donemler yuklenemedi.',
    periodsLoadFailed: 'Donemler alinirken hata olustu.'
  },
  'en-US': {
    period: 'Period',
    company: 'Company',
    loading: 'Loading...',
    companyNotFound: 'No company found',
    periodNotFound: 'No period found',
    companiesLoadError: 'Companies could not be loaded.',
    companiesLoadFailed: 'An error occurred while loading companies.',
    periodsLoadError: 'Periods could not be loaded.',
    periodsLoadFailed: 'An error occurred while loading periods.'
  }
};

const formatDisplayDate = (value, locale = 'tr-TR') => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(locale).format(date);
};

const getPeriodOptionLabel = (period, locale = 'tr-TR') => {
  const fallbackPeriod = UI_TEXT[locale]?.period || UI_TEXT['tr-TR'].period;
  const label = period?.label || period?.description || period?.code || `${fallbackPeriod} ${period?.id}`;
  const start = formatDisplayDate(period?.startDate, locale);
  const end = formatDisplayDate(period?.endDate, locale);
  if (start && end) {
    return `${label} (${start} -- ${end})`;
  }
  return label;
};

function CompanyPeriodSelector({
  onSelectionChange,
  tenantAdminUserId = '',
  locale = '',
  initialCompanyId = '',
  initialPeriodId = '',
  autoSelectFirst = true
}) {
  const { language } = useLanguage();
  const effectiveLocale = locale || language;
  const [companies, setCompanies] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedPeriodId, setSelectedPeriodId] = useState('');
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingPeriods, setLoadingPeriods] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const t = useCallback((key) => UI_TEXT[effectiveLocale]?.[key] || UI_TEXT['tr-TR'][key] || key, [effectiveLocale]);

  const selectedCompany = useMemo(
    () => companies.find((company) => String(company.id) === selectedCompanyId) || null,
    [companies, selectedCompanyId]
  );

  const selectedPeriod = useMemo(
    () => periods.find((period) => String(period.id) === selectedPeriodId) || null,
    [periods, selectedPeriodId]
  );

  const updateSelection = useCallback(() => {
    if (!onSelectionChange) {
      return;
    }

    onSelectionChange({
      company: selectedCompany,
      period: selectedPeriod,
      companyId: selectedCompanyId,
      periodId: selectedPeriodId
    });
  }, [onSelectionChange, selectedCompany, selectedCompanyId, selectedPeriod, selectedPeriodId]);

  const fetchCompanies = useCallback(async (tenantAdminUserIdValue) => {
    setLoadingCompanies(true);
    setErrorMessage('');

    try {
      const params = new URLSearchParams();
      if (tenantAdminUserIdValue) {
        params.set('tenantAdminUserId', String(tenantAdminUserIdValue));
      }
      const query = params.toString();
      const response = await fetch(`${API_BASE_URL}/api/companies${query ? `?${query}` : ''}`, {
        headers: buildHeaders()
      });

      if (!response.ok) {
        throw new Error(t('companiesLoadError'));
      }

      const data = await response.json();
      setCompanies(data || []);
    } catch (caught) {
      setErrorMessage(getErrorMessage(caught, t('companiesLoadFailed')));
    } finally {
      setLoadingCompanies(false);
    }
  }, [t]);

  const fetchPeriods = useCallback(async (companyId) => {
    setLoadingPeriods(true);
    setErrorMessage('');

    try {
      const params = new URLSearchParams();
      if (companyId) {
        params.set('companyId', String(companyId));
      }
      const query = params.toString();

      const response = await fetch(`${API_BASE_URL}/api/periods${query ? `?${query}` : ''}`, {
        headers: buildHeaders()
      });

      if (!response.ok) {
        throw new Error(t('periodsLoadError'));
      }

      const data = await response.json();
      setPeriods(data || []);
    } catch (caught) {
      setErrorMessage(getErrorMessage(caught, t('periodsLoadFailed')));
    } finally {
      setLoadingPeriods(false);
    }
  }, [t]);

  useEffect(() => {
    setCompanies([]);
    setPeriods([]);
    setSelectedCompanyId(initialCompanyId ? String(initialCompanyId) : '');
    setSelectedPeriodId(initialPeriodId ? String(initialPeriodId) : '');
    fetchCompanies(tenantAdminUserId);
  }, [fetchCompanies, initialCompanyId, initialPeriodId, tenantAdminUserId]);

  useEffect(() => {
    if (!selectedCompanyId) {
      return;
    }
    if (loadingCompanies || companies.length === 0) {
      return;
    }
    const exists = companies.some((company) => String(company.id) === selectedCompanyId);
    if (!exists) {
      setSelectedCompanyId('');
      setSelectedPeriodId('');
    }
  }, [companies, loadingCompanies, selectedCompanyId]);

  useEffect(() => {
    if (!autoSelectFirst) {
      return;
    }
    if (!selectedCompanyId && companies.length > 0) {
      setSelectedCompanyId(String(companies[0].id));
    }
  }, [autoSelectFirst, companies, selectedCompanyId]);

  useEffect(() => {
    if (!selectedCompanyId) {
      setPeriods([]);
      setSelectedPeriodId('');
      return;
    }
    fetchPeriods(selectedCompanyId);
  }, [fetchPeriods, selectedCompanyId]);

  useEffect(() => {
    if (loadingPeriods) {
      return;
    }
    if (!selectedPeriodId && periods.length > 0) {
      if (!autoSelectFirst) {
        return;
      }
      setSelectedPeriodId(String(periods[0].id));
      return;
    }

    if (selectedPeriodId && periods.every((period) => String(period.id) !== selectedPeriodId)) {
      setSelectedPeriodId(autoSelectFirst && periods.length > 0 ? String(periods[0].id) : '');
    }
  }, [autoSelectFirst, loadingPeriods, periods, selectedPeriodId]);

  useEffect(() => {
    updateSelection();
  }, [updateSelection]);

  const handleCompanyChange = (event) => {
    setSelectedCompanyId(event.target.value);
    setSelectedPeriodId('');
  };

  const handlePeriodChange = (event) => {
    setSelectedPeriodId(event.target.value);
  };
  const showCompaniesEmpty = !loadingCompanies && companies.length === 0;
  const showPeriodsEmpty = !loadingPeriods && Boolean(selectedCompanyId) && periods.length === 0;

  return (
    <div className="company-period-card">
      {(loadingCompanies || loadingPeriods) && <LoadingState message={t('loading')} />}
      <div className="company-period-grid">
        <div className="company-period-control">
          <label htmlFor="company-period-company">{t('company')}</label>
          <select
            id="company-period-company"
            value={selectedCompanyId}
            onChange={handleCompanyChange}
            disabled={loadingCompanies || companies.length === 0}
          >
            {loadingCompanies ? (
              <option value="">{t('loading')}</option>
            ) : companies.length === 0 ? (
              <option value="">{t('companyNotFound')}</option>
            ) : (
              companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))
            )}
          </select>
        </div>
        <div className="company-period-control">
          <label htmlFor="company-period-period">{t('period')}</label>
          <select
            id="company-period-period"
            value={selectedPeriodId}
            onChange={handlePeriodChange}
            disabled={loadingPeriods || periods.length === 0}
          >
            {loadingPeriods ? (
              <option value="">{t('loading')}</option>
            ) : periods.length === 0 ? (
              <option value="">{t('periodNotFound')}</option>
            ) : (
              periods.map((period) => (
                <option key={period.id} value={period.id}>
                  {getPeriodOptionLabel(period, effectiveLocale)}
                </option>
              ))
            )}
          </select>
        </div>
      </div>
      {showCompaniesEmpty && <EmptyState title={t('companyNotFound')} />}
      {showPeriodsEmpty && <EmptyState title={t('periodNotFound')} />}
      {errorMessage && <p className="placeholder-error">{errorMessage}</p>}
    </div>
  );
}

export default CompanyPeriodSelector;
